import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DistributionHistogram.css';

/**
 * DistributionHistogram
 * Range-aware histogram with a sequenced "wave" rise on mount.
 * Bars inside the acceptable range are green, bars outside are red.
 *
 * Feed it EITHER:
 *   values={number[]} + range={{min,max}}          (bins built to align with the range)
 * OR
 *   histogram={{counts:[], bin_edges:[]}} + range  (backend-computed bins, e.g. Outliers)
 *
 * Props
 *   title            card title (e.g. "Duration distribution")
 *   values           raw numeric values
 *   histogram        {counts, bin_edges} from backend
 *   range            {min, max} acceptable bounds (either may be null/undefined)
 *   unit             short unit for legend text (e.g. "min")
 *   insideLabel      legend text for in-range bars   (default built from range)
 *   outsideLabel     legend text for out-of-range bars (default "outside range")
 *   insight          {tone:'critical'|'warning'|'success'|'neutral', headline, detail}
 *   headerRight      optional node rendered top-right (e.g. a variable picker)
 *   isDarkMode, isMobile
 *   formatTick       fn(number) -> string for axis labels (default: compact)
 *   startDelay       ms to wait after mount before the bars rise (let the card float in first)
 */

const fmtCompact = (v) => {
  if (v === null || v === undefined || !isFinite(v)) return '';
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e4) return `${(v / 1e3).toFixed(0)}k`;
  if (Number.isInteger(v)) return String(v);
  if (a >= 100) return v.toFixed(0);
  if (a >= 10) return v.toFixed(1).replace(/\.0$/, '');
  return v.toFixed(2).replace(/\.?0+$/, '');
};

const niceStep = (raw) => {
  if (!(raw > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  const n = m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10;
  return n * p;
};

/** Build bins aligned to the range so the in/out colour split is exact at the bounds. */
export const buildRangeAlignedBins = (values, range) => {
  const v = (values || []).filter((x) => typeof x === 'number' && isFinite(x));
  if (!v.length) return [];
  const lo = Math.min(...v);
  const hi = Math.max(...v);
  const min = range && typeof range.min === 'number' ? range.min : null;
  const max = range && typeof range.max === 'number' ? range.max : null;

  const edges = [];
  const push = (e) => { if (edges.length === 0 || e > edges[edges.length - 1]) edges.push(e); };

  if (min !== null && max !== null && max > min) {
    // Below min: up to 4 nice bins
    if (lo < min) {
      const step = niceStep((min - Math.max(0, Math.min(lo, 0))) / 4);
      let start = Math.floor(lo / step) * step;
      if (lo >= 0 && start < 0) start = 0;
      for (let e = start; e < min - 1e-9; e += step) push(Number(e.toFixed(6)));
    }
    push(min);
    // Inside range: 5 bins
    const inStep = (max - min) / 5;
    for (let i = 1; i < 5; i++) push(Number((min + inStep * i).toFixed(6)));
    push(max);
    if (hi > max) push(Infinity);
  } else if (min !== null) {
    if (lo < min) {
      const step = niceStep((min - lo) / 4);
      for (let e = Math.floor(lo / step) * step; e < min - 1e-9; e += step) push(Number(e.toFixed(6)));
    }
    push(min);
    const step = niceStep((Math.max(hi, min + 1) - min) / 5);
    for (let e = min + step; e < hi + step; e += step) push(Number(e.toFixed(6)));
  } else if (max !== null) {
    const step = niceStep((max - Math.min(lo, max)) / 5);
    let start = Math.floor(lo / step) * step;
    for (let e = start; e < max - 1e-9; e += step) push(Number(e.toFixed(6)));
    push(max);
    if (hi > max) push(Infinity);
  } else {
    const n = Math.min(12, Math.max(6, Math.round(Math.sqrt(v.length))));
    const step = niceStep((hi - lo) / n) || 1;
    for (let e = Math.floor(lo / step) * step; e <= hi + step; e += step) push(Number(e.toFixed(6)));
  }

  const bins = [];
  for (let i = 0; i < edges.length - 1; i++) {
    const a = edges[i];
    const b = edges[i + 1];
    bins.push({ min: a, max: b, count: 0 });
  }
  v.forEach((x) => {
    for (let i = 0; i < bins.length; i++) {
      const last = i === bins.length - 1;
      if (x >= bins[i].min && (x < bins[i].max || (last && x <= bins[i].max))) { bins[i].count++; break; }
    }
  });
  return bins;
};

const binsFromHistogram = (histogram) => {
  const counts = histogram?.counts || [];
  const edges = histogram?.bin_edges || [];
  if (!counts.length || edges.length !== counts.length + 1) return [];
  return counts.map((c, i) => ({ min: edges[i], max: edges[i + 1], count: c }));
};

/** Collapse very fine backend histograms so bars stay legible on small screens. */
const coalesce = (bins, target) => {
  if (bins.length <= target) return bins;
  const factor = Math.ceil(bins.length / target);
  const out = [];
  for (let i = 0; i < bins.length; i += factor) {
    const chunk = bins.slice(i, i + factor);
    out.push({
      min: chunk[0].min,
      max: chunk[chunk.length - 1].max,
      count: chunk.reduce((s, b) => s + b.count, 0),
    });
  }
  return out;
};

const DistributionHistogram = ({
  title = 'Distribution',
  values = null,
  histogram = null,
  range = null,
  unit = '',
  insideLabel = null,
  outsideLabel = 'outside range',
  insight = null,
  headerRight = null,
  isDarkMode = false,
  isMobile = false,
  formatTick = fmtCompact,
  maxBins = null,
  startDelay = 300,
}) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Sequenced wave: bars are rendered at scaleY(0) first, then after the card has
  // floated in (startDelay) we flip the class on a painted frame so every bar's
  // transition + stagger actually runs instead of being skipped on first paint.
  useEffect(() => {
    let raf1 = null;
    let raf2 = null;
    const timer = setTimeout(() => {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setMounted(true));
      });
    }, Math.max(0, startDelay));
    return () => {
      clearTimeout(timer);
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [startDelay]);

  const min = range && typeof range.min === 'number' ? range.min : null;
  const max = range && typeof range.max === 'number' ? range.max : null;

  const rawBins = useMemo(() => {
    if (histogram) return binsFromHistogram(histogram);
    return buildRangeAlignedBins(values, range);
  }, [values, histogram, range]);

  const bins = useMemo(() => {
    const cap = maxBins || (width < 380 ? 8 : width < 640 ? 12 : 18);
    return coalesce(rawBins, cap);
  }, [rawBins, width, maxBins]);

  const total = bins.reduce((s, b) => s + b.count, 0);
  const peak = Math.max(1, ...bins.map((b) => b.count));

  const inRange = (b) => {
    const center = isFinite(b.max) ? (b.min + b.max) / 2 : b.min + 1;
    if (min !== null && center < min) return false;
    if (max !== null && center > max) return false;
    return true;
  };

  const green = isDarkMode ? '#34d399' : '#059669';
  const red = isDarkMode ? '#f87171' : '#dc2626';
  const textMuted = isDarkMode ? '#6B7280' : '#9CA3AF';
  const text = isDarkMode ? '#E5E7EB' : '#1F2937';

  // Axis label density based on bar width
  const barW = bins.length ? width / bins.length : 0;
  const labelEvery = barW >= 54 ? 1 : barW >= 30 ? 2 : 3;
  const showPct = barW >= 34;

  const tickLabel = (b, i) => {
    if (!isFinite(b.max)) return `${formatTick(b.min)}+`;
    return `${formatTick(b.min)}–${formatTick(b.max)}`;
  };

  const legendInside = insideLabel || (
    min !== null && max !== null ? `within ${formatTick(min)}–${formatTick(max)}${unit ? ` ${unit}` : ''}`
      : min !== null ? `at least ${formatTick(min)}${unit ? ` ${unit}` : ''}`
        : max !== null ? `up to ${formatTick(max)}${unit ? ` ${unit}` : ''}` : 'within range'
  );

  const tone = insight?.tone || 'neutral';

  if (!bins.length || total === 0) {
    return (
      <div className={`dqh-card ${isDarkMode ? 'dqh-dark' : ''}`}>
        <div className="dqh-head">
          <div className="dqh-title">{title}</div>
          {headerRight}
        </div>
        <div className="dqh-empty">No numeric values to plot.</div>
      </div>
    );
  }

  return (
    <div className={`dqh-card ${isDarkMode ? 'dqh-dark' : ''} ${mounted ? 'dqh-on' : ''}`}>
      <div className="dqh-head">
        <div className="dqh-title">{title}</div>
        <div className="dqh-head-right">
          {headerRight}
          <div className="dqh-legend">
            <span className="dqh-legend-item"><i style={{ background: red }} />{outsideLabel}</span>
            <span className="dqh-legend-item"><i style={{ background: green }} />{legendInside}</span>
          </div>
        </div>
      </div>

      <div className="dqh-plot" ref={wrapRef} onMouseLeave={() => setHover(null)}>
        <div className="dqh-bars" style={{ '--dqh-gap': isMobile ? '3px' : '6px' }}>
          {bins.map((b, i) => {
            const ok = inRange(b);
            const h = (b.count / peak) * 100;
            const pct = (b.count / total) * 100;
            const c = ok ? green : red;
            return (
              <div
                key={i}
                className="dqh-col"
                onMouseEnter={() => setHover(i)}
                onTouchStart={() => setHover(i)}
              >
                {showPct && (
                  <div
                    className="dqh-pct"
                    style={{ color: c, transitionDelay: `${180 + i * 55}ms`, bottom: `calc(${h}% + 6px)` }}
                  >
                    {pct >= 1 ? `${Math.round(pct)}%` : pct > 0 ? '<1%' : ''}
                  </div>
                )}
                <div
                  className={`dqh-bar ${hover !== null && hover !== i ? 'dqh-dim' : ''}`}
                  style={{
                    height: `${Math.max(h, b.count > 0 ? 2.5 : 0)}%`,
                    background: c,
                    transitionDelay: `${i * 55}ms`,
                  }}
                />
                {hover === i && (
                  <div className="dqh-tip" style={{ borderColor: `${c}55` }}>
                    <div className="dqh-tip-k">{tickLabel(b, i)}{unit ? ` ${unit}` : ''}</div>
                    <div className="dqh-tip-v" style={{ color: c }}>{b.count.toLocaleString()} <span>({pct.toFixed(1)}%)</span></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="dqh-axis">
          {bins.map((b, i) => (
            <div key={i} className="dqh-tick" style={{ color: textMuted }}>
              {i % labelEvery === 0 || i === bins.length - 1 ? tickLabel(b, i) : ''}
            </div>
          ))}
        </div>
      </div>

      {insight && (insight.headline || insight.detail) && (
        <div className={`dqh-insight dqh-${tone}`} style={{ transitionDelay: `${bins.length * 55 + 200}ms` }}>
          {insight.headline && <strong>{insight.headline}</strong>}
          {insight.detail && <span style={{ color: isDarkMode ? '#D1D5DB' : text }}> {insight.detail}</span>}
        </div>
      )}
    </div>
  );
};

export default DistributionHistogram;